import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useEffectEvent, useState } from 'react'

import { useIdentity } from '~/shell/auth/useIdentity'
import { startFlagClient, stopFlagClient } from '~/shell/integrations/unleash'

import { flagContextFor } from './flagContext'

/**
 * Resolves this session's flag values, reporting when they are settled.
 *
 * Starts only after claims resolve. The effect event reads the latest identity without object changes restarting
 * the effect. EMR sessions may omit `userId` because they have no Auth0 user (Atlas ADR-0028).
 */
export const useSessionFlags = (enabled: boolean): boolean => {
  const { user } = useAuth0()
  const { isAdmin, customerClaim } = useIdentity()
  const [resolved, setResolved] = useState(false)

  const startForIdentity = useEffectEvent(() =>
    startFlagClient(
      flagContextFor({
        isAdmin,
        customerClaim,
        email: user?.email,
        subject: user?.sub,
      }),
    ),
  )

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    let abandoned = false
    // Never rejects: it resolves on toggles, on a failed fetch, and on its own timeout alike.
    void startForIdentity().then(() => {
      if (!abandoned) {
        setResolved(true)
      }
    })

    return () => {
      abandoned = true
      stopFlagClient()
    }
  }, [enabled])

  return resolved
}
